/*
 * Lightweight event emitter for browser-side funnel telemetry.
 *
 * Wraps POST /v1/events. Used by tools to record granular signals
 * (view, phase1_complete, vendor_added, vendor_scored, etc.) that
 * the analytics dashboard reads from the ToolEvents table.
 *
 * - Best-effort: events that fail are dropped silently. They aren't
 *   queued for retry. Submissions are the source of truth; events
 *   are signal supplements.
 * - Anonymous: server records an IpHash + UserAgent server-side. No
 *   user-supplied PII expected in `data`.
 * - Bounded: server caps EventDataJson at 8 KB. Don't dump full
 *   submission payloads here — those have their own endpoint.
 *
 * Lives in the API client folder so the public tools can import it
 * with the same path they use for `api.submit`.
 */

import { getClientId } from '@shared/clientId';

// Same .env.<mode> source as the rest of the API client. No localhost
// fallback — see client.ts for the rationale.
const BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;

export interface EventBody {
  tool_id: string;
  event_type: string;
  data?: Record<string, unknown>;
}

/**
 * Fire an event. Returns a promise that always resolves — failures
 * are caught and dropped because callers should never have to
 * try/catch around telemetry.
 */
export async function recordEvent(body: EventBody): Promise<void> {
  try {
    await fetch(`${BASE_URL}/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Id': getClientId(),
      },
      body: JSON.stringify(body),
      // Use keepalive so events fire on page-leave (e.g., the buyer
      // closes the tab right after phase1_complete). Browsers cap the
      // total keepalive payload at 64KB; we're well under.
      keepalive: true,
    });
  } catch {
    /* dropped; telemetry is best-effort */
  }
}
